import { Href, useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useRef } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { env } from '@/util/env';
import { buildApiLaunchUrl, createBinding, hashBinding, storeBinding } from '@/util/smartLaunch';

/**
 * SMART EHR launch entry (/auth/smart/launch).
 *
 * Health systems register this URL with their EHR, so it is permanent. The screen creates a
 * binding, keeps it in this tab's sessionStorage, and hands the launch to the API in the same
 * frame: a new window would break out of the EHR's iframe and leave the binding behind.
 */
export default function SmartLaunchScreen() {
  const { replace } = useRouter();

  const { iss, launch } = useLocalSearchParams<{ iss?: string; launch?: string }>();

  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;

    if (!iss || !launch) {
      replace(`${ROUTES.SMART_ERROR}?reason=expired` as Href);

      return;
    }

    const binding = createBinding();

    storeBinding(binding);

    hashBinding(binding)
      .then((bindingHash) => {
        window.location.assign(
          buildApiLaunchUrl({ apiBaseUrl: env.EXPO_PUBLIC_BASE_API_URL, iss, launch, bindingHash })
        );
      })
      .catch(() => replace(`${ROUTES.SMART_ERROR}?reason=server_error` as Href));
  }, [iss, launch, replace]);

  return (
    <VStack className="flex-1 items-center justify-center gap-6 p-6">
      <Spinner size="large" />
      <Text className="text-center font-semibold text-xl">Opening Primum…</Text>
    </VStack>
  );
}
